// TipTap node for broadcast merge tags — the per-recipient personalization
// tokens the email sender substitutes at send time. Serializes to/from
// exactly `{ type: 'mergeTag', attrs: { name } }`, the shape the server's
// `BroadcastDocSchema` (apps/server/src/_platform/email/broadcast-doc-schema.ts)
// validates; the allowed names come from the shared registry so adding a tag
// there lights it up on both sides.
//
// Atom semantics: the chip is a single non-editable unit — the caret can't
// enter it, Backspace removes it whole, and copy/paste round-trips it via
// the `data-type`/`data-name` attributes (the label text inside the span is
// presentation only and ignored on parse).

import { mergeAttributes, Node } from '@tiptap/core';
import { Plugin, type Transaction } from '@tiptap/pm/state';
import {
  MERGE_TAG_LABELS,
  MERGE_TAG_NAMES,
  type MergeTagName,
} from '@leanin/shared/email/merge-tags';

import styles from './BroadcastBodyEditor.module.css';

function isMergeTagName(v: unknown): v is MergeTagName {
  return (
    typeof v === 'string' && (MERGE_TAG_NAMES as readonly string[]).includes(v)
  );
}

export const MergeTag = Node.create({
  name: 'mergeTag',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      name: {
        default: MERGE_TAG_NAMES[0],
        parseHTML: (element: HTMLElement) => {
          const v = element.getAttribute('data-name');
          return isMergeTagName(v) ? v : MERGE_TAG_NAMES[0];
        },
        renderHTML: (attributes) => ({
          'data-name': attributes['name'] as MergeTagName,
        }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-type="merge-tag"]' }];
  },

  renderHTML({ node, HTMLAttributes }) {
    const name = node.attrs['name'] as MergeTagName;
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'merge-tag',
        class: styles['mergeTag'],
      }),
      MERGE_TAG_LABELS[name],
    ];
  },

  // Plain-text clipboard / getText() representation.
  renderText({ node }) {
    return MERGE_TAG_LABELS[node.attrs['name'] as MergeTagName];
  },

  // ProseMirror lets marks land on inline atoms (the PARENT's mark set
  // governs, so a node-spec `marks: ''` on a leaf is inert) — a select-all
  // bold would put `marks` on the mergeTag node. The server's MergeTagNode
  // Zod schema has no `marks` key and strips it silently, so the mark would
  // vanish on save — a WYSIWYG lie. This normalizer keeps the atom mark-free
  // in the editor model, so getJSON() is always exactly
  // `{ type: 'mergeTag', attrs: { name } }`.
  addProseMirrorPlugins() {
    const type = this.type;
    return [
      new Plugin({
        appendTransaction: (transactions, _oldState, newState) => {
          if (!transactions.some((tr) => tr.docChanged)) return null;
          let tr: Transaction | null = null;
          newState.doc.descendants((node, pos) => {
            if (node.type === type && node.marks.length > 0) {
              tr = (tr ?? newState.tr).setNodeMarkup(
                pos,
                undefined,
                node.attrs,
                [],
              );
            }
          });
          return tr;
        },
      }),
    ];
  },
});
